import { portalText } from "../../../../i18n/portal.js";
/**
 * The order book over the real API — P12-03.
 *
 * Every call goes through the generated client, which has a method per documented
 * `operationId` and no escape hatch, so this file structurally cannot reach an endpoint the
 * spec does not carry.
 *
 * ONE THING IT DOES THAT NO OTHER GATEWAY SLICE DOES: it resolves the shop first.
 * `/v1/orders/**` is keyed by `shop`, the console is looking at one VoiceAgent, and
 * `GET /v1/orders/summary` is the operation that maps one to the other. So the summary is
 * fetched and its `shop_id` is cached per VoiceAgent for the life of the gateway — a shop
 * does not move between agents, and a second round trip on every page of a table would be a
 * round trip to learn something that has not changed.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import { readRecord, readText } from "../../../../api/decode.js";
import {
  customerOrderDetailOfWire,
  customerOrderRowOfWire,
  customerOrderSummaryOfWire,
  formatPlacedAt,
  matchesCustomerOrderSearch,
  serverAwaitsPaymentOfFilter,
  serverStatusOfFilter,
  type CounterAccessKeyView,
  type ShopWaitTimeView,
  type CustomerOrderDetailView,
  type CustomerOrderPageView,
  type CustomerOrderSummaryView,
  type OrderBookGateway,
  type OrderTicketView,
} from "./order-book-gateway.js";

export interface OrderBookApiSettings {
  readonly client: WeTalkApiClient;
  /** The API's own origin, for the two URLs the browser navigates to rather than fetches. */
  readonly baseUrl: string;
  /**
   * Where the public site lives — the Shop view's origin.
   *
   * Required, with no fallback: a console that guessed its own origin would produce an
   * "Open shop screen" link to a page it does not serve, and a shop owner would find out by
   * putting a tablet on a counter.
   */
  readonly publicSiteBaseUrl: string;
}

export function orderBookApiGateway(settings: OrderBookApiSettings): OrderBookGateway {
  const client = settings.client;
  const shopIdFor = new Map<string, string>();

  const summaryOf = async (voiceAgentId: string): Promise<CustomerOrderSummaryView> => {
    const body = assertOk(await client.summariseCustomerOrders({ query: {} }));
    const summary = customerOrderSummaryOfWire(body, "summariseCustomerOrders");
    // A workspace with no shop yet answers `shop_id: null` (review #97); nothing to remember.
    if (summary.shopId !== null) shopIdFor.set(voiceAgentId, summary.shopId);
    return summary;
  };

  const shopFor = async (voiceAgentId: string): Promise<string | null> => {
    const held = shopIdFor.get(voiceAgentId);
    if (held !== undefined) return held;
    return (await summaryOf(voiceAgentId)).shopId;
  };

  const patch = async (reference: string, body: Record<string, unknown>): Promise<CustomerOrderDetailView> =>
    customerOrderDetailOfWire(
      assertOk(
        await client.patchCustomerOrder({
          path: { customer_order_reference: reference },
          query: {},
          body,
        }),
      ),
      "patchCustomerOrder",
    );

  return {
    async listCustomerOrders(request): Promise<CustomerOrderPageView> {
      const shop = await shopFor(request.voiceAgentId);
      // No shop yet is an empty book, not a request that would answer 404.
      if (shop === null) return { row: [], nextCursor: null };
      const query: Record<string, string> = { shop };
      // "Awaiting payment" and "New" both ask for `received`, and the SERVER splits them by
      // `awaits_payment` (OD-P6 (a); plan-005 review item 6), so each chip pages through its own orders.
      const status = serverStatusOfFilter(request.filter);
      if (status !== null) query["status"] = status;
      const awaitsPayment = serverAwaitsPaymentOfFilter(request.filter);
      if (awaitsPayment !== null) query["awaits_payment"] = awaitsPayment;
      if (request.cursor !== null) query["cursor"] = request.cursor;

      const body = readRecord(
        assertOk(await client.listCustomerOrders({ query })),
        "listCustomerOrders",
      );
      const data = Array.isArray(body["data"]) ? body["data"] : [];
      const row = data
        .map((one, index) => customerOrderRowOfWire(one, `listCustomerOrders.data[${String(index)}]`))
        // The search box filters the page that is loaded, not the table. There is no
        // documented search parameter, and a box that looked like it searched everything
        // while searching one page would be the worse of the two lies — the screen says so.
        .filter((one) => matchesCustomerOrderSearch(one, request.search));

      const cursor = body["next_cursor"];
      return { row, nextCursor: typeof cursor === "string" ? cursor : null };
    },

    getCustomerOrderSummary: summaryOf,

    async getCustomerOrder(reference): Promise<CustomerOrderDetailView> {
      const body = assertOk(
        await client.getCustomerOrder({ path: { customer_order_reference: reference }, query: {} }),
      );
      return customerOrderDetailOfWire(body, "getCustomerOrder");
    },

    // `next`, never a target status: the console moves an order one step forward. The only
    // other moves it can make are a cancellation, which must say why, and the one-step undo
    // of the latest move (review #123) — so a mis-click is reversible and nothing else is.
    advanceCustomerOrder: (reference) => patch(reference, { status: "next" }),
    // A paid online order must say whether its money goes back (FR-PAY-12; the API answers
    // `refund_decision_required` otherwise). Every other order carries no such field.
    cancelCustomerOrder: (reference, reason, refundPayment) =>
      patch(
        reference,
        refundPayment === null
          ? { status: "cancelled", reason }
          : { status: "cancelled", reason, refund_payment: refundPayment },
      ),
    undoCustomerOrderMove: (reference) => patch(reference, { undo: true }),
    markCustomerOrderPaid: (reference) => patch(reference, { paid: true }),

    async printOrderTicket(reference): Promise<OrderTicketView> {
      const body = readRecord(
        assertOk(
          await client.printOrderTicket({
            path: { customer_order_reference: reference },
            query: {},
            body: {},
          }),
        ),
        "printOrderTicket",
      );
      return {
        printedAtText: formatPlacedAt(readText(body["printed_at"], "printOrderTicket.printed_at")),
        plainText: readText(body["plain_text"], "printOrderTicket.plain_text"),
      };
    },

    async rotateCounterAccessKey(shopId): Promise<CounterAccessKeyView> {
      const body = readRecord(
        assertOk(await client.rotateShopCounterAccessKey({ path: { shop_id: shopId }, body: {} })),
        "rotateShopCounterAccessKey",
      );
      return {
        shopId: readText(body["shop_id"], "rotateShopCounterAccessKey.shop_id"),
        counterAccessKey: readText(
          body["counter_access_key"],
          "rotateShopCounterAccessKey.counter_access_key",
        ),
        notice: readText(body["notice"], "rotateShopCounterAccessKey.notice"),
      };
    },

    async shopWaitTime(shopId): Promise<ShopWaitTimeView> {
      const body = readRecord(assertOk(await client.getShopWaitTime({ path: { shop_id: shopId } })), "getShopWaitTime");
      return waitTimeOf(body, "getShopWaitTime");
    },

    async setShopWaitTime(shopId, wait): Promise<ShopWaitTimeView> {
      const body = readRecord(
        assertOk(
          await client.setShopWaitTime({
            path: { shop_id: shopId },
            body: { delivery_minute: wait.deliveryMinute, pickup_minute: wait.pickupMinute },
          }),
        ),
        "setShopWaitTime",
      );
      return waitTimeOf(body, "setShopWaitTime");
    },

    customerOrderExportUrl(voiceAgentId): string {
      // A URL the browser navigates to, not a fetch: the response is a file with a
      // `Content-Disposition`, and pulling it through `fetch` to re-attach a filename in
      // JavaScript would be re-implementing what the header already does.
      const shop = shopIdFor.get(voiceAgentId);
      const query = shop === undefined ? "" : `?shop=${encodeURIComponent(shop)}`;
      return `${settings.baseUrl}/v1/orders/export.csv${query}`;
    },

    shopScreenUrl(counterAccessKey): string {
      // THE KEY GOES IN THE FRAGMENT. A fragment is never sent to a server, so the
      // capability stays out of every access log, `Referer` header and CDN cache key
      // between the tablet and the origin.
      return `${settings.publicSiteBaseUrl}/shop/#${encodeURIComponent(counterAccessKey)}`;
    },
  };
}

/** The wire's `{ delivery_minute, pickup_minute }`, read strictly. */
function waitTimeOf(body: Record<string, unknown>, at: string): ShopWaitTimeView {
  const minute = (field: string): number => {
    const value = body[field];
    if (typeof value !== "number" || !Number.isInteger(value)) throw new Error(portalText("operations", "value_value_is_not_a_whole_number_811cfff5", { v0: String(at), v1: String(field) }));
    return value;
  };
  return { deliveryMinute: minute("delivery_minute"), pickupMinute: minute("pickup_minute") };
}
